<template>
  <aw-tab-schematic
    :key="splits.map(s => s.checkoutType).join('_')"
    :selected-id="selectedTab"
    :aria-labelledby="ariaLabelledby"
    :allow-close-on-click="false"
    :class="$style.orderSplitTab"
    :style="`
      --awOrderSplitTabTabCount: ${splits.length}
    `"
    @update:selected-id="emits('update:selected-tab', $event)"
  >
    <template
      v-for="{ checkoutType } in splits"
      :key="checkoutType"
      #[`tab_${checkoutType}`]="slotProps"
    >
      <div
        v-bind="slotProps.attrs"
        :class="[
          $style.tab,
          {
            [$style.tabIsSelected]: slotProps.isSelected,
          }
        ]"
        v-text="tabTitles[checkoutType]"
      />
    </template>
    <template
      v-for="{ checkoutType } in splits"
      :key="checkoutType"
      #[`tabpanel_${checkoutType}`]="slotProps"
    >
      <div
        v-bind="slotProps.attrs"
        :class="[
          $style.tabpanel,
          {
            [$style.tabpanelIsFirst]: slotProps.tabIdx === 0,
            [$style.tabpanelIsLast]: slotProps.tabIdx === splits.length - 1,
          },
        ]"
      >
        <template v-if="slotProps.isSelected">
          <div
            v-for="sp in splits"
            :key="sp.checkoutType"
            :class="[
              $style.nestedTab,
              {
                [$style.nestedTabIsSelected]: sp.checkoutType === checkoutType,
              },
            ]"
            :aria-hidden="(sp.checkoutType !== checkoutType) || null"
          >
            <div :class="$style.tabControl" @click="emits('update:selected-tab', `aw_${sp.checkoutType}`)">
              <component :is="tabHeadingTag" :class="$style.tabControlHeading">
                {{ tabTitles[sp.checkoutType] }}
              </component>
              <div
                :class="$style.tabControlCount"
                v-html="$awt(
                  'aw.basket_tab.split_item_count',
                  {
                    count: splitItemCounts[sp.checkoutType],
                  },
                )"
              />
              <div :class="$style.tabControlActions">
                <template v-if="sp.checkoutType === checkoutType">
                  <aw-favourite-version2
                    :is-multiple="true"
                    :products="splitItems[sp.checkoutType]"
                  />
                  <button
                    type="button"
                    :aria-label="$awt('aw.basket_tab.split_delete')"
                    :class="['buttonReset', $style.actionButton]"
                    @click="onDeleteSplit"
                  >
                    <span
                      :class="$style.actionButtonText"
                      v-text="$awt('aw.basket_tab.split_delete')"
                    />
                    <lv-icon
                      name="trash-version2-16"
                      :size="16"
                      :class="$style.actionButtonIconSmall"
                    />
                    <lv-icon
                      name="trash-version2-16"
                      :size="24"
                      :class="$style.actionButtonIconBig"
                    />
                  </button>
                </template>
                <template v-else>
                  <span
                    :class="$style.splitSum"
                    v-text="splitSums[sp.checkoutType]"
                  />
                </template>
              </div>
              <div :class="$style.tabControlSplitIcon">
                <lv-icon
                  v-if="splitIcons[sp.checkoutType]"
                  :name="splitIcons[sp.checkoutType]"
                  :size="16"
                />
              </div>
            </div>
          </div>
          <aw-warning-modal :title="$awt('aw.basket.truncate_warning_modal.title')" :message="$awt('aw.basket.truncate_warning_modal.text')" @close-modal="hideWarningModal">
            <template #actionButtons>
              <div :class="$style.deleteSplitActionButtons">
                <lv-button
                  type="button"
                  styling="primary-dark"
                  :shrink="true"
                  :stretch="true"
                  @click="deleteSplit(checkoutType)"
                >
                  {{ $awt('aw.global.yes') }}
                </lv-button>
                <lv-button
                  type="button"
                  styling="secondary-dark"
                  :shrink="true"
                  :stretch="true"
                  @click="hideWarningModal"
                >
                  {{ $awt('aw.global.cancel') }}
                </lv-button>
              </div>
            </template>
          </aw-warning-modal>
          <div
            :class="[
              $style.nestedTabpanel,
              {
                [$style.nestedTabpanelSingle]: numberOfSplits < 2,
              },
            ]"
          >
            <aw-order-split-cart :checkout-type="checkoutType" heading-tag="h3">
              <template #splitTop>
                <slot name="splitTop" />
              </template>
            </aw-order-split-cart>
          </div>
        </template>
      </div>
    </template>
  </aw-tab-schematic>
</template>

<script setup>
  import { defineAsyncComponent, withModifiers } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { calculateHeadingChildren } from '~~/common/utils/headingTag';
  import { useOrderSplit } from '~~/common/composables/useOrderSplit';
  import { LvIcon, LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwOrderSplitCart from '~~/shop/app/components/AwOrderSplitCart.vue';
  import AwWarningModal from '~~/common/components/Common/Modals/AwWarningModal.vue';
  import { useModalStore } from '~~/common/stores/modal';
  import { useOrderStore } from '~~/common/stores/order';

  const AwTabSchematic = defineAsyncComponent(() => import('~~/common/components/Common/AwTabSchematic.vue'));
  const AwFavouriteVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwFavouriteVersion2.vue'));

  const props = defineProps({
    ariaLabelledby: {
      type: String,
      required: true,
    },
    headingTag: {
      type: String,
      required: true,
      default: 'h2',
    },
    selectedTab: {
      type: [null, String],
      required: true,
      default: null,
    },
  });

  const emits = defineEmits(['update:selected-tab']);

  const {
    $awt,
    $price,
  } = useNuxtApp();

  const { splits } = useOrderSplit();

  const { hideWarningModal } = useModalStore();

  const numberOfSplits = computed(() => {
    return splits.value.length || 0;
  });

  const tabTitles = computed(() => {
    return {
      FOOD: $awt('aw.basket_tab.tab.title.food'),
      NONFOOD: $awt('aw.basket_tab.tab.title.nonfood'),
    };
  });

  const tabHeadingTag = computed(() => {
    return calculateHeadingChildren(props.headingTag);
  });

  const splitItemCounts = computed(() => {
    return splits.value.reduce(
      (acc, split) => {
        acc[split.checkoutType] = split.packages.reduce(
          (splitItemNum, pkg) => {
            return splitItemNum += pkg.items.length;
          },
          0,
        );
        return acc;
      },
      {},
    );
  });

  const splitItems = computed(() => {
    return splits.value.reduce(
      (acc, split) => {
        acc[split.checkoutType] = split.packages.reduce(
          (splitItems1, pkg) => {
            return splitItems1 = [
              ...
                pkg.items,
            ];
          },
          {},
        );
        return acc;
      },
      {},
    );
  });


  const splitSums = computed(() => {
    return splits.value.reduce(
      (acc, split) => {
        const sum = split?.summary?.sum || {};
        acc[split.checkoutType] = $price(
          {
            gross: sum.grossDiscounted || 0,
          },
          sum.currency,
        );
        return acc;
      },
      {},
    );
  });

  const splitIcons = {
    FOOD: 'hamburger-16',
    NONFOOD: 'monitor-16',
  };

  const onDeleteSplit = withModifiers(() => {
    const { showWarningModal } = useModalStore();
    showWarningModal();
  }, ['stop']);

  const deleteSplit = async (checkoutType) => {
    const { removeAllItem } = useOrderStore();
    await removeAllItem(checkoutType);
    hideWarningModal();
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
@use "sass:color";

.orderSplitTab {
  display: grid;
  grid-template-columns: repeat(var(--awOrderSplitTabTabCount), auto) 1fr;
  column-gap: 3px;
}

.tab {
  font-weight: bold;
  grid-row: 1;
  border: 1px solid transparent;

  &:not(:focus-visible, :hover) {
    @include awSrOnly($position: "absolute");
  }

  &:focus-visible {
    border-color: currentColor;
  }

  &IsSelected {
    text-decoration: underline;
  }
}

.tabpanel {
  grid-row: 2;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(var(--awOrderSplitTabTabCount), 1fr);

  &:focus-visible {
    outline: 1px solid currentColor;
  }

  &IsFirst {
    .nestedTabpanel {
      border-top-left-radius: 0;
    }
  }

  &IsLast {
    .nestedTabpanel {
      border-top-right-radius: 0;
    }
  }
}

.nestedTab {
  grid-row: 1;
  padding: 8px;
  border-radius: 16px 16px 0 0;

  &IsSelected {
    background: $color-white;

    .tabControl {
      color: $color-text-sage;
      background: $color-sage-450--12;
    }

    .tabControlSplitIcon {
      background: $color-sage-450--12;
    }
  }

  &:not(&IsSelected) {
    .tabControl {
      cursor: pointer;
      background: $color-white;
      box-shadow: 0 2px 8px 0 color.change($color-olive-750, $alpha: 0.08);
    }
  }

  @include tablet(min) {
    padding: 12px;
    border-radius: 24px 24px 0 0;
  }
  @include desktop-medium(min) {
    padding: 24px;
  }
}

.nestedTabpanel {
  --awBasketTabSidePadding: 12px;
  grid-row: 2;
  grid-column: 1 / -1;
  padding: var(--awBasketTabSidePadding);
  border-radius: 16px;
  background: $color-white;

  @include tablet(min) {
    padding: 24px var(--awBasketTabSidePadding) 12px;
    border-radius: 24px;
  }
  @include desktop-small(min) {
    padding: var(--awBasketTabSidePadding);
  }
  @include desktop-medium(min) {
    --awBasketTabSidePadding: 24px;
  }

  &Single {
    padding-top: 0 !important;
  }
}

.tabControl {
  display: grid;
  grid-template-areas:
    "icon heading"
    "icon count"
    "icon actions";
  grid-template-columns: min-content auto;
  gap: 0 8px;
  padding: 8px;
  border-radius: 12px;

  @include tablet(min) {
    grid-template-areas:
      "icon heading count"
      "actions actions actions";
    gap: 12px;
    padding: 12px;
    border-radius: 16px;
  }
  @include desktop-medium(min) {
    grid-template-areas:
      "icon heading actions"
      "icon count actions";
    gap: 4px 12px;
    padding: 20px 16px;

    @include desktop-medium(max) {
      grid-template-columns: min-content auto min-content;
    }
  }
}

.tabControlHeading {
  grid-area: heading;
  @include font(900, 10px, 16px);

  @include tablet(min) {
    @include font(null, 16px, 20px);
    align-self: center;
  }
  @include desktop-medium(min) {
    @include font(null, 18px, 24px);
  }
}

.tabControlCount {
  grid-area: count;
  @include font(500, 10px, 16px);

  @include tablet(min) {
    @include font(400, 12px, null);
    justify-self: end;
    align-self: center;
  }
  @include desktop-medium(min) {
    @include font(500, 14px, 20px);
    justify-self: start;
  }
}

.tabControlActions {
  @include font(700, 10px, 16px);
  grid-area: actions;
  justify-self: end;
  display: flex;
  gap: 16px;
  padding: 6px 0 2px;

  @include tablet(min) {
    gap: 12px;
    padding: 0;
  }

  @include desktop-medium(min) {
    @include font(null, 12px, 16px);
    align-self: center;
    gap: 24px;
  }
}

.tabControlSplitIcon {
  display: flex;
  align-self: start;
  padding: 6px;
  border-radius: 8px;
  background: color.change($color-olive-750, $alpha: 0.12);
  grid-area: icon;

  @include tablet(min) {
    padding: 8px;
  }

  @include desktop-medium(min) {
    align-self: center;
  }
}

.splitSum {
  @include font(900, 12px, 16px);

  @include tablet(min) {
    @include font(null, 18px, 24px);
  }

  @include desktop-medium(min) {
    @include font(null, 22px, 28px);
  }
}

.actionButton {
  display: flex;
  align-items: center;
  text-align: left;
  text-decoration: underline;
  border-radius: 8px;
  gap: 4px;

  @include desktop-medium(min) {
    text-decoration: none;
    gap: 8px;
  }

  &:focus-visible {
    outline: 2px solid $color-black;
  }
}

.actionButtonText {
  @include mobile(max) {
    display: none;
  }
}

.actionButtonIcon {
  &Small,
  &Big {
    flex-shrink: 0;
  }

  &Small {
    @include desktop-medium(min) {
      display: none;
    }
  }

  &Big {
    @include desktop-small(max) {
      display: none;
    }
  }
}

.deleteSplitActionButtons {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
</style>
